import React, { useEffect, useState } from 'react';
import { useWedding } from '../context/WeddingContext';
import { getTranslation } from '../i18n';
import { getAuthenticatedSupabase } from '../supabaseClient';
import { Guest, WeddingTeamMemberPayload } from '../types';
import { Modal } from '../components/Modal';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Toast, ToastMessage } from '../components/Toast';
import { Edit2, Trash2, User } from 'lucide-react';

type Slot = 'bride' | 'groom' | 'coordinator';

const SLOT_ROLES: Record<Slot, Guest['role']> = {
  bride: 'bride',
  groom: 'groom',
  coordinator: 'wedding_coordinator',
};

export default function WeddingTeam() {
  const { wedding, locale } = useWedding();
  const t = (key: string) => getTranslation(locale, key);

  const [bride, setBride] = useState<Guest | null>(null);
  const [groom, setGroom] = useState<Guest | null>(null);
  const [coordinator, setCoordinator] = useState<Guest | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingSlot, setEditingSlot] = useState<Slot | null>(null);
  const [deleteSlot, setDeleteSlot] = useState<Slot | null>(null);
  const [formData, setFormData] = useState<WeddingTeamMemberPayload>({ full_name: '' });
  const [toast, setToast] = useState<ToastMessage | null>(null);

  const showToast = (type: 'success' | 'error', text: string) => {
    setToast({ id: String(Date.now()), type, text });
    setTimeout(() => setToast(null), 5000);
  };

  const fetchTeam = async () => {
    if (!wedding) return;
    const supabase = getAuthenticatedSupabase();
    const { data } = await supabase
      .from('guests')
      .select('*')
      .eq('wedding_id', wedding.id)
      .in('role', ['bride', 'groom', 'wedding_coordinator']);
    const list = data || [];
    setBride(list.find((g: Guest) => g.role === 'bride') || null);
    setGroom(list.find((g: Guest) => g.role === 'groom') || null);
    setCoordinator(list.find((g: Guest) => g.role === 'wedding_coordinator') || null);
    setLoading(false);
  };

  useEffect(() => {
    fetchTeam();
  }, [wedding]);

  const getSlotData = (slot: Slot): Guest | null => {
    if (slot === 'bride') return bride;
    if (slot === 'groom') return groom;
    return coordinator;
  };

  const openEdit = (slot: Slot) => {
    const g = getSlotData(slot);
    setFormData({
      full_name: g?.full_name ?? '',
      contact_email: g?.contact_email ?? '',
      contact_phone: g?.contact_phone ?? '',
      allergies: g?.allergies ?? '',
      notes: g?.notes ?? '',
    });
    setEditingSlot(slot);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!wedding || !editingSlot) return;
    const supabase = getAuthenticatedSupabase();
    const role = SLOT_ROLES[editingSlot];
    const payload = {
      full_name: formData.full_name.trim(),
      contact_email: formData.contact_email || null,
      contact_phone: formData.contact_phone || null,
      allergies: formData.allergies || null,
      notes: formData.notes || null,
      wedding_id: wedding.id,
      role,
      rsvp_status: 'confirmed',
      plus_one: false,
      kids_count: 0,
      side: 'mutual',
      counts_for_capacity: true,
      visible_in_guests: editingSlot === 'coordinator',
      category: editingSlot === 'coordinator' ? 'guest' : 'wedding_team',
    };
    const existing = getSlotData(editingSlot);
    if (existing) {
      const { error } = await supabase.from('guests').update(payload).eq('id', existing.id);
      if (error) {
        showToast('error', `Chyba: ${error.message}`);
        return;
      }
      showToast('success', t('common.save') + ' – ' + (editingSlot === 'bride' ? t('wedding_team.bride') : editingSlot === 'groom' ? t('wedding_team.groom') : t('wedding_team.coordinator')));
    } else {
      const { error } = await supabase.from('guests').insert(payload);
      if (error) {
        showToast('error', `Chyba: ${error.message}`);
        return;
      }
      showToast('success', t('common.save') + ' – ' + (editingSlot === 'bride' ? t('wedding_team.bride') : editingSlot === 'groom' ? t('wedding_team.groom') : t('wedding_team.coordinator')));
    }
    setEditingSlot(null);
    fetchTeam();
  };

  const handleDelete = async () => {
    if (!wedding || !deleteSlot) return;
    const g = getSlotData(deleteSlot);
    if (!g?.id) {
      setDeleteSlot(null);
      return;
    }
    const supabase = getAuthenticatedSupabase();
    const { error } = await supabase.from('guests').delete().eq('id', g.id);
    if (error) {
      showToast('error', `Chyba: ${error.message}`);
      setDeleteSlot(null);
      return;
    }
    const slotLabel = deleteSlot === 'bride' ? t('wedding_team.bride') : deleteSlot === 'groom' ? t('wedding_team.groom') : t('wedding_team.coordinator');
    showToast('success', slotLabel + ' – ' + t('common.delete'));
    setDeleteSlot(null);
    fetchTeam();
  };

  if (!wedding) return null;

  const card = (slot: Slot, label: string, colorClass: string, bgClass: string) => {
    const g = getSlotData(slot);
    const isSet = !!g?.full_name;
    return (
      <div className={`rounded-2xl border border-ds-border overflow-hidden ${bgClass}`}>
        <div className="p-4">
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="flex items-center gap-3 min-w-0">
              <div className={`p-2 rounded-xl shrink-0 ${colorClass}`}>
                <User size={18} />
              </div>
              <h3 className="font-semibold text-slate-900 dark:text-white truncate">{label}</h3>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button
                type="button"
                onClick={() => openEdit(slot)}
                className="p-1.5 text-slate-400 hover:text-brand-600 dark:hover:text-brand-400 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                title={t('wedding_team.add_edit')}
                aria-label={t('wedding_team.add_edit')}
              >
                <Edit2 size={16} />
              </button>
              {isSet && (
                <button
                  type="button"
                  onClick={() => setDeleteSlot(slot)}
                  className="p-1.5 text-slate-400 hover:text-red-500 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                  title={t('common.delete')}
                  aria-label={t('common.delete')}
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400 pl-11">
            {isSet ? g!.full_name : t('wedding_team.not_set')}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8 animate-fade-in pb-24">
      <div>
        <h1 className="text-ds-h1 text-slate-900 dark:text-white">{t('wedding_team.title')}</h1>
        <p className="text-ds-body text-slate-500 dark:text-slate-400 mt-2">{t('wedding_team.subtitle')}</p>
      </div>

      {loading ? (
        <p className="text-slate-500">{t('common.loading')}</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {card('bride', t('wedding_team.bride'), 'text-pink-600 bg-pink-100 dark:bg-pink-900/20', 'bg-white dark:bg-slate-900')}
          {card('groom', t('wedding_team.groom'), 'text-blue-600 bg-blue-100 dark:bg-blue-900/20', 'bg-white dark:bg-slate-900')}
          {card('coordinator', t('wedding_team.coordinator'), 'text-emerald-600 bg-emerald-100 dark:bg-emerald-900/20', 'bg-white dark:bg-slate-900')}
        </div>
      )}

      <Modal
        isOpen={!!editingSlot}
        onClose={() => setEditingSlot(null)}
        title={editingSlot ? `${t('wedding_team.add_edit')} – ${editingSlot === 'bride' ? t('wedding_team.bride') : editingSlot === 'groom' ? t('wedding_team.groom') : t('wedding_team.coordinator')}` : ''}
      >
        <form onSubmit={handleSave} className="space-y-6">
          <div>
            <label className="text-ds-meta text-slate-500 mb-2 block">{t('wedding_team.name')} *</label>
            <input
              required
              type="text"
              value={formData.full_name}
              onChange={e => setFormData({ ...formData, full_name: e.target.value })}
              className="w-full p-4 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <div>
            <label className="text-ds-meta text-slate-500 mb-2 block">{t('wedding_team.email')}</label>
            <input
              type="email"
              value={formData.contact_email || ''}
              onChange={e => setFormData({ ...formData, contact_email: e.target.value })}
              className="w-full p-4 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <div>
            <label className="text-ds-meta text-slate-500 mb-2 block">{t('wedding_team.phone')}</label>
            <input
              type="tel"
              value={formData.contact_phone || ''}
              onChange={e => setFormData({ ...formData, contact_phone: e.target.value })}
              className="w-full p-4 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <div>
            <label className="text-ds-meta text-slate-500 mb-2 block">{t('wedding_team.allergies')}</label>
            <input
              type="text"
              value={formData.allergies || ''}
              onChange={e => setFormData({ ...formData, allergies: e.target.value })}
              className="w-full p-4 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <div>
            <label className="text-ds-meta text-slate-500 mb-2 block">{t('wedding_team.notes')}</label>
            <textarea
              value={formData.notes || ''}
              onChange={e => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
              className="w-full p-4 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <button type="submit" className="w-full bg-brand-600 text-white py-4 rounded-xl hover:bg-brand-700 transition-colors">
            {t('common.save')}
          </button>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteSlot}
        title={t('common.delete')}
        description={t('common.confirm_delete')}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        onConfirm={handleDelete}
        onCancel={() => setDeleteSlot(null)}
        variant="danger"
      />

      {toast && <Toast toast={toast} onClose={() => setToast(null)} />}
    </div>
  );
}
